import { Children, useState } from "react";
import { createPortal } from "react-dom";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

import { cn } from "@/lib/utils";
import styles from "./DraggableContainer.module.scss";

const itemId = index => `draggable-${index}`;

const gripClasses =
  "tw:cursor-grab tw:border-none tw:bg-transparent tw:p-1 tw:text-(--color-text-muted) tw:hover:text-foreground tw:active:cursor-grabbing";

const wrapperClasses = direction =>
  cn(
    "tw:items-center",
    direction === "horizontal" ? "tw:inline-flex" : "tw:flex"
  );

const SortableItem = ({ id, direction, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
  };

  // The activator lives on the grip, not the wrapper: children here are
  // interactive (the one consumer passes a Checkbox), and @dnd-kit has no
  // equivalent of react-beautiful-dnd's interactive-element blocking. Binding
  // setActivatorNodeRef is what makes KeyboardSensor reject a Space keydown
  // that came from the child instead of consuming it as a drag start.
  return (
    <div ref={setNodeRef} className={wrapperClasses(direction)} style={style}>
      <button
        type="button"
        data-drag-handle
        aria-label="Drag to reorder"
        className={gripClasses}
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="tw:size-4" />
      </button>
      {children}
    </div>
  );
};

function DraggableContainer(props) {
  const children = Children.toArray(props.children);
  const [activeId, setActiveId] = useState(null);
  const direction = props.direction || "vertical";
  const ids = children.map((_, index) => itemId(index));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    // Without the sortable coordinate getter, arrow keys translate the node by
    // a flat 25px — less than an item, so the drag never crosses into the next
    // index and keyboard reordering silently does nothing.
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragStart = event => {
    setActiveId(event.active.id);
    if (props.onDragStart) {
      props.onDragStart(event);
    }
  };

  const handleDragEnd = ({ active, over }) => {
    setActiveId(null);
    if (!over || active.id === over.id) {
      return;
    }
    props.onUpdateChildrenOrder(
      active.id,
      ids.indexOf(active.id),
      ids.indexOf(over.id)
    );
  };

  const activeIndex = activeId === null ? -1 : ids.indexOf(activeId);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      <SortableContext
        items={ids}
        strategy={
          direction === "horizontal"
            ? horizontalListSortingStrategy
            : verticalListSortingStrategy
        }
      >
        <div
          className={`${styles["draggable-container"]} ${
            props.className || ""
          } ${activeId !== null ? "dragging" : ""}`}
        >
          {children.map((child, index) => (
            <SortableItem
              key={itemId(index)}
              id={itemId(index)}
              direction={direction}
            >
              {child}
            </SortableItem>
          ))}
        </div>
      </SortableContext>
      {/* Overlay goes to the body so the dragged item escapes the container's
          clipping and transforms, as it did under react-beautiful-dnd. */}
      {createPortal(
        <DragOverlay>
          {activeIndex >= 0 ? (
            <div className={styles["draggable-container"]}>
              <div className={wrapperClasses(direction)}>
                <span data-drag-handle className={gripClasses}>
                  <GripVertical className="tw:size-4" />
                </span>
                {children[activeIndex]}
              </div>
            </div>
          ) : null}
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  );
}

export default DraggableContainer;
